import {Document as PDFDocument, Page, pdfjs} from 'react-pdf';
import React, {useEffect, useRef, useState} from 'react';
import '../drivers/AnnotationLayer.css';
import '../drivers/TextLayer.css';
import {
    ArrowUturnLeftIcon,
    ArrowUturnRightIcon,
    InformationCircleIcon,
    MagnifyingGlassMinusIcon,
    MagnifyingGlassPlusIcon
} from "@heroicons/react/24/outline";
import {ChevronLeftIcon, ChevronRightIcon, DocumentDuplicateIcon, DocumentIcon} from "@heroicons/react/20/solid";
import MagnifyingGlassIcon from "@heroicons/react/24/outline/MagnifyingGlassIcon";
import Tippy from "@tippyjs/react";
import {classNames} from "../../../../util/util-helpers";
import {LoaderLarge} from "../../../loader";

pdfjs.GlobalWorkerOptions.workerSrc = new URL(
    'pdfjs-dist/build/pdf.worker.min.mjs',
    import.meta.url,
).toString();

export default function PDFViewerNew({filePath, defaultZoom = 1.5, onError}) {
    const [numPages, setNumPages] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [value, setValue] = useState(1);
    const [rotation, setRotation] = useState(0);
    const [pageNumber, setPageNumber] = useState(1);
    const [isLoading, setIsLoading] = useState(true);
    const [isSinglePageView, setIsSinglePageView] = useState(false);
    const minZoom = 0.5;
    const maxZoom = 3;
    const [zoom, setZoom] = useState(defaultZoom);

    const scrollContainerRef = useRef(null);
    const vScrollRef = useRef(null);
    const isDragging = useRef(false);
    const dragStartX = useRef(0);
    const dragStartY = useRef(0);
    const scrollStartX = useRef(0);
    const scrollStartY = useRef(0);
    const ctrlHeld = useRef(false);

    const zoomIn = () => setZoom(z => Math.min(z + 0.2, maxZoom));
    const zoomOut = () => setZoom(z => Math.max(z - 0.2, minZoom));
    const zoomReset = () => setZoom(defaultZoom);

    const handleKeyDown = (e) => {
        if (e.key === 'Control' || e.key === 'Meta') {
            ctrlHeld.current = true;
            if (scrollContainerRef.current) scrollContainerRef.current.style.cursor = 'grab';
        }
    };

    const handleKeyUp = (e) => {
        if (e.key === 'Control' || e.key === 'Meta') {
            ctrlHeld.current = false;
            if (scrollContainerRef.current && !isDragging.current) scrollContainerRef.current.style.cursor = '';
        }
    };

    const handleWindowBlur = () => {
        ctrlHeld.current = false;
        if (scrollContainerRef.current && !isDragging.current) scrollContainerRef.current.style.cursor = '';
    };

    const handleWheelZoom = (e) => {
        if (!e.ctrlKey && !e.metaKey) return;
        e.preventDefault();
        const delta = e.deltaY > 0 ? -0.1 : 0.1;
        setZoom(z => Math.round(Math.min(maxZoom, Math.max(minZoom, z + delta)) * 10) / 10);
    };

    const handleDragStart = (e) => {
        if (e.button !== 0) return;
        if (!e.ctrlKey && !e.metaKey) return;
        e.preventDefault();
        const el = scrollContainerRef.current;
        isDragging.current = true;
        dragStartX.current = e.clientX;
        dragStartY.current = e.clientY;
        scrollStartX.current = el.scrollLeft;

        let vEl = el;
        if (el.scrollHeight <= el.clientHeight) {
            let parent = el.parentElement;
            while (parent && parent !== document.body) {
                if (parent.scrollHeight > parent.clientHeight) {
                    vEl = parent;
                    break;
                }
                parent = parent.parentElement;
            }
        }
        vScrollRef.current = vEl;
        scrollStartY.current = vEl.scrollTop;
        el.style.cursor = 'grabbing';
    };

    const handleDragMove = (e) => {
        if (!isDragging.current) return;
        const el = scrollContainerRef.current;
        el.scrollLeft = scrollStartX.current - (e.clientX - dragStartX.current);
        const vEl = vScrollRef.current;
        if (vEl) {
            vEl.scrollTop = scrollStartY.current - (e.clientY - dragStartY.current);
        }
    };

    const handleDragEnd = () => {
        if (!isDragging.current) return;
        isDragging.current = false;
        vScrollRef.current = null;
        scrollContainerRef.current.style.cursor = ctrlHeld.current ? 'grab' : '';
    };

    function onDocumentLoadSuccess({numPages}) {
        setNumPages(numPages);
        setCurrentPage(1);
        setValue(1);
        setIsLoading(false);
    }

    function onDocumentLoadError(error) {
        setIsLoading(false);
        if (onError) onError(error);
    }

    const handlePageLoad = async (pdfPage) => {
        const rotate = pdfPage.rotate ?? 0

        if (pdfPage.rotate && !rotation) {
            setRotation(rotate);
        }
    };

    useEffect(() => {
        const el = scrollContainerRef.current;
        if (!el) return;
        el.addEventListener('wheel', handleWheelZoom, {passive: false});
        el.addEventListener('mousedown', handleDragStart);
        document.addEventListener('mousemove', handleDragMove);
        document.addEventListener('mouseup', handleDragEnd);
        document.addEventListener('keydown', handleKeyDown);
        document.addEventListener('keyup', handleKeyUp);
        window.addEventListener('blur', handleWindowBlur);
        return () => {
            el.removeEventListener('wheel', handleWheelZoom);
            el.removeEventListener('mousedown', handleDragStart);
            document.removeEventListener('mousemove', handleDragMove);
            document.removeEventListener('mouseup', handleDragEnd);
            document.removeEventListener('keydown', handleKeyDown);
            document.removeEventListener('keyup', handleKeyUp);
            window.removeEventListener('blur', handleWindowBlur);
        };
    }, []);

    return (
        <div className="h-full flex flex-col">
            <div
                className="shrink-0 sticky left-0 top-0 mb-2 z-20 inline-flex items-center gap-x-4 gap-1 bg-inverse py-1 px-4 w-full border-b border-dashed border-tm-gray-300">

                {isSinglePageView && (
                    <>
                        <button
                            onClick={() => setPageNumber((p) => Math.max(p - 1, 1))}
                            disabled={pageNumber === 1}
                            className="flex items-center px-3 py-1.5 bg-primary text-white rounded-md hover:bg-blue-700 disabled:bg-tm-gray-400 disabled:cursor-not-allowed"
                        >
                            <ChevronLeftIcon className="h-5 w-5 mr-1"/>
                            Prev
                        </button>

                        <span className="text-gray-700 font-medium">
                    Page {pageNumber} of {numPages}
            </span>

                        <button
                            onClick={() => setPageNumber((p) => Math.min(p + 1, numPages))}
                            disabled={pageNumber === numPages}
                            className="flex items-center px-3 py-1.5 bg-primary text-white rounded-md hover:bg-blue-700 disabled:bg-tm-gray-400 disabled:cursor-not-allowed"
                        >
                            Next
                            <ChevronRightIcon className="h-5 w-5 ml-1"/>
                        </button>
                    </>
                )}

                <button
                    onClick={zoomOut}
                    className="w-9 h-9 flex items-center justify-center rounded-btn text-primary hover:bg-primary-transparent disabled:text-tm-gray-600 disabled:bg-tm-gray-200 disabled:opacity-25"
                    disabled={zoom <= minZoom}
                >
                    <MagnifyingGlassMinusIcon className={'w-7 h-7'}/>
                </button>

                <button
                    onClick={zoomReset}
                    className="w-9 h-9 flex items-center justify-center rounded-btn text-primary hover:bg-primary-transparent disabled:text-tm-gray-600 disabled:bg-tm-gray-200 disabled:opacity-25"
                >
                    <MagnifyingGlassIcon className={'w-7 h-7 z-10'}/>
                </button>

                <button
                    onClick={zoomIn}
                    className="w-9 h-9 flex items-center justify-center rounded-btn text-primary hover:bg-primary-transparent disabled:text-tm-gray-600 disabled:bg-tm-gray-200 disabled:opacity-25"
                    disabled={zoom >= maxZoom}
                >
                    <MagnifyingGlassPlusIcon className={'w-7 h-7'}/>
                </button>

                <button
                    onClick={() => setRotation((r) => (r - 90) % 360)}
                    className="w-9 h-9 flex items-center justify-center rounded-btn text-primary hover:bg-primary-transparent disabled:text-tm-gray-600 disabled:bg-tm-gray-200 disabled:opacity-25"
                >
                    <ArrowUturnLeftIcon className={'w-7 h-7'}/>
                </button>

                <button
                    onClick={() => setRotation((r) => (r + 90) % 360)}
                    className="w-9 h-9 flex items-center justify-center rounded-btn text-primary hover:bg-primary-transparent disabled:text-tm-gray-600 disabled:bg-tm-gray-200 disabled:opacity-25"
                >
                    <ArrowUturnRightIcon className={'w-7 h-7'}/>
                </button>

                {numPages > 1 && (
                    <button
                        onClick={() => {
                            setRotation(0);
                            setZoom(defaultZoom);
                            setIsSinglePageView(!isSinglePageView)
                        }}
                        className="w-9 h-9 flex items-center justify-center rounded-btn text-primary hover:bg-primary-transparent disabled:text-tm-gray-600 disabled:bg-tm-gray-200 disabled:opacity-25"
                        aria-label="Toggle single/all pages view"
                    >
                        {!isSinglePageView ? (
                            <DocumentIcon className="w-7 h-7"/>
                        ) : (
                            <DocumentDuplicateIcon className="w-7 h-7"/>
                        )}
                    </button>
                )}

                <Tippy content="Hold Ctrl (⌘ on Mac) to zoom with scroll wheel or drag to pan">
                    <span className="ml-auto w-9 h-9 flex items-center justify-center text-tm-gray-400 cursor-default">
                        <InformationCircleIcon className="w-5 h-5"/>
                    </span>
                </Tippy>
            </div>
            <div className="flex-1 flex flex-col min-h-0">
                {!!isLoading && (
                    <div key={'pdfLoading'} className={"grid justify-center items-center"}><LoaderLarge disableContainer={true}/></div>)}

                <div ref={scrollContainerRef} className="flex-1 overflow-auto min-h-0">
                    <PDFDocument
                        file={filePath}
                        onLoadSuccess={onDocumentLoadSuccess}
                        onLoadError={onDocumentLoadError}
                        loading={null}
                    >
                        {isSinglePageView ? (
                            <div className="grid align-middle">
                                <Page pageNumber={pageNumber} renderTextLayer scale={zoom} rotate={rotation}
                                      className="mx-auto" loading={null} onLoadSuccess={handlePageLoad}/>
                            </div>
                        ) : (
                            Array.from(new Array(numPages), (el, index) => (
                                <div
                                    key={`outer_page_container_${index + 1}`}
                                    className={classNames("grid align-middle", index > 0 ? "mt-4 border-t-8 border-tm-gray-300 border-dashed pt-4 w-full" : undefined)}>
                                    <div
                                        key={`page_container_${index + 1}`}
                                        className="flex justify-center"
                                    >
                                        <Page key={`page_${index + 1}`} pageNumber={index + 1} renderTextLayer
                                              scale={zoom}
                                              className={"border-2 border-tm-gray-300"}
                                              rotate={rotation} renderAnnotationLayer={true} loading={null} onLoadSuccess={handlePageLoad}/>
                                    </div>
                                </div>
                            ))
                        )}
                    </PDFDocument>
                </div>

                {isSinglePageView && (
                    <div className={"flex justify-center align-middle py-2"}>
                        <button
                            onClick={() => setPageNumber((p) => Math.max(p - 1, 1))}
                            disabled={pageNumber === 1}
                            className="flex items-center px-3 py-1.5 bg-primary text-white rounded-md hover:bg-blue-700 disabled:bg-tm-gray-400 disabled:cursor-not-allowed"
                        >
                            <ChevronLeftIcon className="h-5 w-5 mr-1"/>
                            Prev
                        </button>

                        <div className="flex text-gray-700 font-medium items-center mx-4">
                            Page {pageNumber} of {numPages}
                        </div>

                        <button
                            onClick={() => setPageNumber((p) => Math.min(p + 1, numPages))}
                            disabled={pageNumber === numPages}
                            className="flex items-center px-3 py-1.5 bg-primary text-white rounded-md hover:bg-blue-700 disabled:bg-tm-gray-400 disabled:cursor-not-allowed"
                        >
                            Next
                            <ChevronRightIcon className="h-5 w-5 ml-1"/>
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}